import { Badge, Tooltip } from 'antd';
import { UnreadBadge } from './unread-badge';
import { getDotColor } from '@modules/members/utils/get-dot-color';
import { useProfile } from '@components/profile-dropdown/hooks/use-profile-dropdown';
import { useScreenSize } from '@shared/hooks/use-screen';
import { useGetWorkspaceTimezone } from '@shared/hooks/use-get-workspace-timezone';
import { AppAvatar } from '@components/app-avatar';
import { IconCrown } from '@/assets/static/icons';
import { RiAtLine, RiShieldUserLine } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';
import { SidebarMemberRow } from '@modules/members/members.types';
import { ChatPreviewLine } from './chat-preview-line';
import { useChatPreviewTime } from '@modules/members/hooks/use-chat-preview-time';

export function MemberCard(member: SidebarMemberRow) {
  const { isMobile } = useScreenSize();
  const { transformDateFormat } = useGetWorkspaceTimezone();
  const { icon, message, awayTill } = member?.userOnlineManagement || {};
  const { loggedInUserId } = useProfile();

  const isSelf = member?.isSelf ?? member?.user?.id === loggedInUserId;
  const showLabel = member?.roles?.[0]?.label === 'Owner' ? 'OWNER' : null;
  const formattedDate = transformDateFormat(awayTill as string, 'MMM D, YYYY, hh:mm A');
  const isActive = member.userOnlineManagement?.isActive;
  const role = member?.roles?.[0]?.label;
  const { t } = useTranslation();
  const chat = member?.chat;

  const displayDateTime = useChatPreviewTime(chat?.at);

  return (
    <div key={member?.id} className="flex gap-2.5 items-center h-9.5">
      <Badge dot style={{ backgroundColor: getDotColor(isActive) }} offset={isMobile ? [-5, 24] : [-6, 31]}>
        <AppAvatar
          className="shrink-0 size-7! lg:size-9! border-gray-500!"
          src={member?.user?.profilePicture?.url}
          name={member?.user?.name}
        />
      </Badge>

      <div className="flex-1 min-w-0 flex flex-col justify-center leading-4">
        <div className="flex items-center justify-between gap-2">
          <span className="flex gap-2 items-center min-w-0 leading-5 text-[15px] lg:text-base text-text-700 group-[.ant-menu-item-selected]:text-text-800">
            <span className="truncate">{member?.user?.name}</span>
            {isSelf ? <span className="shrink-0">({t('you').toUpperCase()})</span> : null}

            {role === 'Owner' && <IconCrown className="shrink-0 size-5" />}
            {role === 'Admin' && <RiShieldUserLine size={20} className="shrink-0 text-primary-400" />}
            <Tooltip
              title={
                <p className="flex flex-col">
                  <span className="text-xs font-medium text-[13px] text-text-950">{message}</span>
                  <span className="font-normal text-text-600 text-xs capitalize!">
                    {t('until_date', { date: formattedDate })}
                  </span>
                </p>
              }
              placement="bottom"
            >
              <span>{icon}</span>
            </Tooltip>
          </span>
          {displayDateTime ? <span className="shrink-0 text-xs text-text-500">{displayDateTime}</span> : null}
        </div>

        <div className="flex items-center justify-between gap-2">
          <ChatPreviewLine chat={chat} fallback={showLabel ?? role} />
          <span className="flex shrink-0 items-center gap-1.5">
            {chat?.isMentioned ? <RiAtLine className="size-4.5 text-primary-500" /> : null}
            <UnreadBadge count={member?.unreadCounter} />
          </span>
        </div>
      </div>
    </div>
  );
}
